import { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../utils/api';
import toast from 'react-hot-toast';
import RichEditor from '../components/editor/RichEditor';
import { formatIST, istLocalToUTC, utcToISTLocal } from '../utils/dateUtils';

const TABS = ['Settings', 'Contacts', 'Emails', 'Preview & Launch'];

export default function SequenceEditor() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [tab, setTab] = useState(0);
  const [seq, setSeq] = useState(null);
  const [aliases, setAliases] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  // Settings state
  const [name, setName] = useState('');
  const [fromEmail, setFromEmail] = useState('');
  const [includeSignature, setIncludeSignature] = useState(true);
  const [openTracking, setOpenTracking] = useState(true);
  const [description, setDescription] = useState('');

  // Contacts state
  const [csvColumns, setCsvColumns] = useState([]);
  const [contactCount, setContactCount] = useState(0);
  const [csvFilename, setCsvFilename] = useState('');
  const [uploading, setUploading] = useState(false);
  const [attachFilename, setAttachFilename] = useState('');
  const [uploadingAttach, setUploadingAttach] = useState(false);
  const [contactMode, setContactMode] = useState('csv'); // 'csv' or 'manual'
  const [manualEmails, setManualEmails] = useState('');
  const [addingManual, setAddingManual] = useState(false);

  // Emails state
  const [emails, setEmails] = useState([{
    step_number: 1, subject: '', body: '', scheduled_at: '', delay_days: 0, delay_hours: 0
  }]);

  const load = useCallback(async () => {
    try {
      const [seqRes, aliasRes] = await Promise.all([
        api.get(`/sequences/${id}`),
        api.get('/auth/gmail/aliases').catch(() => ({ data: [] }))
      ]);
      const s = seqRes.data;
      setSeq(s);
      setName(s.name);
      setFromEmail(s.from_email || '');
      setIncludeSignature(s.include_signature);
      setOpenTracking(s.open_tracking);
      setDescription(s.description || '');
      setCsvColumns(s.csv_columns || []);
      setContactCount(parseInt(s.total_contacts) || 0);
      setCsvFilename(s.csv_filename || '');
      setAttachFilename(s.attachment_filename || '');
      if (s.emails?.length > 0) {
        setEmails(s.emails.map(e => ({
          step_number: e.step_number,
          subject: e.subject || '',
          body: e.body || '',
          scheduled_at: e.scheduled_at ? utcToISTLocal(e.scheduled_at) : '',
          delay_days: e.delay_days || 0,
          delay_hours: e.delay_hours || 0
        })));
      }
      setAliases(aliasRes.data);
    } catch (err) {
      toast.error('Failed to load sequence');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { load(); }, [load]);

  const saveSettings = async () => {
    setSaving(true);
    try {
      await api.put(`/sequences/${id}`, { name, from_email: fromEmail, include_signature: includeSignature, open_tracking: openTracking, description });
      toast.success('Settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const saveEmails = async () => {
    setSaving(true);
    try {
      const emailsToSave = emails.map(e => ({
        ...e,
        scheduled_at: e.scheduled_at ? istLocalToUTC(e.scheduled_at) : ''
      }));
      await api.put(`/sequences/${id}/emails`, { emails: emailsToSave });
      toast.success('Emails saved');
    } catch { toast.error('Failed to save emails'); }
    finally { setSaving(false); }
  };

  const uploadCsv = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploading(true);
    const form = new FormData();
    form.append('csv', file);
    try {
      const res = await api.post(`/sequences/${id}/csv`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setCsvColumns(res.data.columns);
      setContactCount(res.data.count);
      setCsvFilename(file.name);
      toast.success(`${res.data.count} contacts imported`);
    } catch (err) { toast.error(err.response?.data?.error || 'Upload failed'); }
    finally { setUploading(false); e.target.value = ''; }
  };

  const addManualContacts = async (replace = false) => {
    const emailList = manualEmails.split(/[\n,;]+/).map(e => e.trim()).filter(Boolean);
    if (emailList.length === 0) return toast.error('Enter at least one email');
    setAddingManual(true);
    try {
      const res = await api.post(`/sequences/${id}/contacts/manual`, { emails: emailList, replace });
      setContactCount(res.data.total);
      setManualEmails('');
      toast.success(`${res.data.added} contacts added (${res.data.total} total)`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed');
    } finally {
      setAddingManual(false);
    }
  };

  const uploadAttachment = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setUploadingAttach(true);
    const form = new FormData();
    form.append('attachment', file);
    try {
      await api.post(`/sequences/${id}/attachment`, form, { headers: { 'Content-Type': 'multipart/form-data' } });
      setAttachFilename(file.name);
      toast.success('Attachment uploaded');
    } catch { toast.error('Upload failed'); }
    finally { setUploadingAttach(false); e.target.value = ''; }
  };

  const removeAttachment = async () => {
    try {
      await api.delete(`/sequences/${id}/attachment`);
      setAttachFilename('');
      toast.success('Attachment removed');
    } catch { toast.error('Failed'); }
  };

  const addFollowUp = () => {
    setEmails(prev => [...prev, {
      step_number: prev.length + 1, subject: '', body: '', scheduled_at: '', delay_days: 3, delay_hours: 0
    }]);
  };

  const removeFollowUp = (idx) => {
    setEmails(prev => prev.filter((_, i) => i !== idx).map((e, i) => ({ ...e, step_number: i + 1 })));
  };

  const updateEmail = (idx, field, value) => {
    setEmails(prev => prev.map((e, i) => i === idx ? { ...e, [field]: value } : e));
  };

  const launch = async () => {
    if (!window.confirm(`Launch sequence to ${contactCount} contacts?`)) return;
    try {
      const emailsToSave = emails.map(e => ({
        ...e,
        scheduled_at: e.scheduled_at ? istLocalToUTC(e.scheduled_at) : ''
      }));
      await api.put(`/sequences/${id}/emails`, { emails: emailsToSave });
      const res = await api.post(`/sequences/${id}/launch`);
      toast.success(`🚀 Launched! ${res.data.contactsScheduled} emails scheduled.`);
      navigate(`/sequences/${id}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Launch failed');
    }
  };

  // For the datetime min attribute in IST
  const nowISTLocal = utcToISTLocal(new Date().toISOString());

  if (loading) return <div className="page-loader"><div className="spinner" /></div>;

  return (
    <div className="main-content">
      <div className="page-header">
        <div>
          <div className="flex-center gap-8" style={{ marginBottom: 4 }}>
            <button className="btn btn-ghost btn-sm" onClick={() => navigate('/')}>← Back</button>
            <span style={{ color: 'var(--text3)' }}>/</span>
            <span style={{ fontSize: 13, color: 'var(--text2)' }}>{name}</span>
          </div>
          <div className="page-title">Edit Sequence</div>
        </div>
        {tab === 3 ? (
          <button className="btn btn-primary btn-lg" onClick={launch}>🚀 Launch Sequence</button>
        ) : (
          <button className="btn btn-secondary" onClick={tab === 0 ? saveSettings : tab === 2 ? saveEmails : null} disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </button>
        )}
      </div>

      <div className="page-body">
        <div className="tabs">
          {TABS.map((t, i) => (
            <button key={t} className={`tab ${tab === i ? 'active' : ''}`} onClick={() => setTab(i)}>{t}</button>
          ))}
        </div>

        {/* TAB 0: Settings */}
        {tab === 0 && (
          <div style={{ maxWidth: 560 }}>
            <div className="form-group">
              <label className="form-label">Sequence Name</label>
              <input className="input" value={name} onChange={e => setName(e.target.value)} />
            </div>
            <div className="form-group">
              <label className="form-label">Private Notes <span style={{color:'var(--text3)', fontWeight:400}}>(only visible to you)</span></label>
              <textarea
                className="input"
                rows={3}
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="e.g. Targeting consulting firms — 2nd batch, following up after campus fair..."
                style={{ resize: 'vertical', minHeight: 80 }}
              />
            </div>
            <div className="form-group">
              <label className="form-label">Send From</label>
              {aliases.length > 0 ? (
                <select className="select" value={fromEmail} onChange={e => setFromEmail(e.target.value)}>
                  <option value="">Select sender...</option>
                  {aliases.map(a => (
                    <option key={a.email} value={a.email}>{a.name ? `${a.name} <${a.email}>` : a.email}</option>
                  ))}
                </select>
              ) : (
                <input className="input" value={fromEmail} onChange={e => setFromEmail(e.target.value)} placeholder="Connect Gmail in Settings to see aliases" />
              )}
            </div>
            <div className="form-group">
              <div className="flex-between">
                <div>
                  <div className="form-label" style={{ marginBottom: 2 }}>Include Signature</div>
                  <div className="form-hint" style={{ marginTop: 0 }}>Append your Gmail signature to every email</div>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={includeSignature} onChange={e => setIncludeSignature(e.target.checked)} />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
            <div className="form-group">
              <div className="flex-between">
                <div>
                  <div className="form-label" style={{ marginBottom: 2 }}>Open Tracking</div>
                  <div className="form-hint" style={{ marginTop: 0 }}>Track when recipients open emails</div>
                </div>
                <label className="toggle">
                  <input type="checkbox" checked={openTracking} onChange={e => setOpenTracking(e.target.checked)} />
                  <span className="toggle-slider" />
                </label>
              </div>
            </div>
            <button className="btn btn-primary" onClick={saveSettings} disabled={saving}>
              {saving ? 'Saving...' : 'Save Settings'}
            </button>
          </div>
        )}

        {/* TAB 1: Contacts */}
        {tab === 1 && (
          <div style={{ maxWidth: 560 }}>
            {/* Contact count */}
            <div className="flex-between" style={{ marginBottom: 12 }}>
              <div style={{ fontWeight: 600, fontSize: 14 }}>
                Contacts: <span style={{ color: 'var(--accent)' }}>{contactCount}</span>
              </div>
            </div>

            {/* Mode toggle */}
            <div className="flex-center gap-8" style={{ marginBottom: 16 }}>
              <button
                className={`btn btn-sm ${contactMode === 'csv' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setContactMode('csv')}
              >
                📂 CSV Upload
              </button>
              <button
                className={`btn btn-sm ${contactMode === 'manual' ? 'btn-primary' : 'btn-secondary'}`}
                onClick={() => setContactMode('manual')}
              >
                ✏️ Manual Entry
              </button>
            </div>

            {/* CSV Upload mode */}
            {contactMode === 'csv' && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div className="flex-between" style={{ marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, marginBottom: 3 }}>Contact List (CSV)</div>
                    <div className="text-xs">CSV must have an "email" column. Other columns become variables.</div>
                  </div>
                  {csvFilename && <span className="badge badge-active">✓ Loaded</span>}
                </div>

                {csvFilename ? (
                  <div>
                    <div className="alert alert-success" style={{ marginBottom: 12 }}>
                      📄 {csvFilename} — {contactCount} contacts imported
                    </div>
                    {csvColumns.length > 0 && (
                      <div style={{ marginBottom: 12 }}>
                        <div className="form-label" style={{ marginBottom: 6 }}>Available Variables</div>
                        <div className="flex-center" style={{ flexWrap: 'wrap', gap: 6 }}>
                          {csvColumns.map(c => (
                            <span key={c} className="var-chip">{`{{${c}}}`}</span>
                          ))}
                        </div>
                      </div>
                    )}
                    <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer' }}>
                      {uploading ? 'Uploading...' : 'Replace CSV'}
                      <input type="file" accept=".csv" style={{ display: 'none' }} onChange={uploadCsv} />
                    </label>
                  </div>
                ) : (
                  <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex' }}>
                    {uploading ? <><span className="spinner" style={{width:14,height:14}} /> Uploading...</> : '📂 Upload CSV'}
                    <input type="file" accept=".csv" style={{ display: 'none' }} onChange={uploadCsv} disabled={uploading} />
                  </label>
                )}
              </div>
            )}

            {/* Manual Entry mode */}
            {contactMode === 'manual' && (
              <div className="card" style={{ marginBottom: 16 }}>
                <div style={{ fontWeight: 600, marginBottom: 4 }}>Manual Email Entry</div>
                <div className="text-xs" style={{ marginBottom: 12 }}>Paste emails — one per line, or comma/semicolon separated.</div>
                <div className="form-group">
                  <textarea
                    className="input"
                    rows={6}
                    value={manualEmails}
                    onChange={e => setManualEmails(e.target.value)}
                    placeholder={"john@example.com\njane@company.com\nbob@org.com"}
                    style={{ resize: 'vertical', minHeight: 120, fontFamily: 'monospace', fontSize: 13 }}
                  />
                  <div className="form-hint">
                    {manualEmails.split(/[\n,;]+/).map(e => e.trim()).filter(e => e && e.includes('@')).length} valid email(s) detected
                  </div>
                </div>
                <div className="flex-center gap-8">
                  <button
                    className="btn btn-primary btn-sm"
                    onClick={() => addManualContacts(false)}
                    disabled={addingManual}
                  >
                    {addingManual ? 'Adding...' : '+ Add to existing'}
                  </button>
                  <button
                    className="btn btn-danger btn-sm"
                    onClick={() => {
                      if (window.confirm('This will replace ALL existing contacts. Continue?')) addManualContacts(true);
                    }}
                    disabled={addingManual}
                  >
                    Replace all & add
                  </button>
                </div>
              </div>
            )}

            {/* Attachment */}
            <div className="card">
              <div style={{ fontWeight: 600, marginBottom: 4 }}>Attachment</div>
              <div className="text-xs" style={{ marginBottom: 12 }}>One file attached to every email in this sequence (e.g. resume, brochure)</div>
              {attachFilename ? (
                <div className="flex-center gap-8">
                  <div className="alert alert-info" style={{ flex: 1 }}>📎 {attachFilename}</div>
                  <button className="btn btn-danger btn-sm" onClick={removeAttachment}>Remove</button>
                </div>
              ) : (
                <label className="btn btn-secondary" style={{ cursor: 'pointer', display: 'inline-flex' }}>
                  {uploadingAttach ? <><span className="spinner" style={{width:14,height:14}} /> Uploading...</> : '📎 Attach File'}
                  <input type="file" style={{ display: 'none' }} onChange={uploadAttachment} disabled={uploadingAttach} />
                </label>
              )}
            </div>
          </div>
        )}

        {/* TAB 2: Emails */}
        {tab === 2 && (
          <div style={{ maxWidth: 720 }}>
            {csvColumns.length === 0 && (
              <div className="alert alert-warning" style={{ marginBottom: 16 }}>
                ⚠️ Upload a CSV first to use personalization variables in your emails
              </div>
            )}

            <div className="steps">
              {emails.map((email, idx) => (
                <div key={idx} className="step-item">
                  <div className="step-line">
                    <div className="step-dot" style={{ background: idx === 0 ? 'var(--accent)' : 'var(--text3)' }} />
                    <div className="step-connector" />
                  </div>
                  <div className="step-content">
                    <div className="card" style={{ marginBottom: 0 }}>
                      <div className="flex-between" style={{ marginBottom: 14 }}>
                        <div style={{ fontWeight: 600, fontSize: 14 }}>
                          {idx === 0 ? '✉️ Initial Email' : `🔁 Follow-up ${idx}`}
                        </div>
                        {idx > 0 && (
                          <button className="btn btn-danger btn-sm" onClick={() => removeFollowUp(idx)}>Remove</button>
                        )}
                      </div>

                      {/* Schedule */}
                      {idx === 0 ? (
                        <div className="form-group">
                          <label className="form-label">Send Date & Time <span style={{ color: 'var(--text3)', fontWeight: 400, fontSize: 11 }}>(IST — India Standard Time)</span></label>
                          <input
                            type="datetime-local"
                            className="input"
                            value={email.scheduled_at}
                            onChange={e => updateEmail(idx, 'scheduled_at', e.target.value)}
                            min={nowISTLocal}
                          />
                          <div className="form-hint">
                            When to send the initial email (IST)
                            {email.scheduled_at && (
                              <span style={{ marginLeft: 8, color: 'var(--accent)' }}>
                                → {formatIST(istLocalToUTC(email.scheduled_at))}
                              </span>
                            )}
                          </div>
                        </div>
                      ) : (
                        <div className="grid-2" style={{ marginBottom: 16 }}>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label">Delay (Days)</label>
                            <input type="number" className="input" min="0" value={email.delay_days}
                              onChange={e => updateEmail(idx, 'delay_days', parseInt(e.target.value) || 0)} />
                          </div>
                          <div className="form-group" style={{ marginBottom: 0 }}>
                            <label className="form-label">Delay (Hours)</label>
                            <input type="number" className="input" min="0" max="23" value={email.delay_hours}
                              onChange={e => updateEmail(idx, 'delay_hours', parseInt(e.target.value) || 0)} />
                          </div>
                          <div className="form-hint" style={{ gridColumn: '1/-1' }}>
                            Sent {email.delay_days}d {email.delay_hours}h after previous email — only if no reply received
                          </div>
                        </div>
                      )}

                      {/* Subject */}
                      <div className="form-group">
                        <label className="form-label">Subject</label>
                        <input className="input" value={email.subject}
                          onChange={e => updateEmail(idx, 'subject', e.target.value)}
                          placeholder={idx === 0 ? 'e.g. Internship Application — {{name}}' : `Re: ${emails[0]?.subject || 'your email'}`} />
                      </div>

                      {/* Body */}
                      <div className="form-group">
                        <label className="form-label">Email Body</label>
                        <RichEditor
                          value={email.body}
                          onChange={val => updateEmail(idx, 'body', val)}
                          variables={csvColumns}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            <div className="flex-center gap-8" style={{ marginTop: 8 }}>
              <button className="btn btn-secondary" onClick={addFollowUp}>+ Add Follow-up</button>
              <button className="btn btn-primary" onClick={saveEmails} disabled={saving}>
                {saving ? 'Saving...' : 'Save All Emails'}
              </button>
            </div>
          </div>
        )}

        {/* TAB 3: Preview & Launch */}
        {tab === 3 && (
          <div style={{ maxWidth: 600 }}>
            <div className="card" style={{ marginBottom: 16 }}>
              <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 16 }}>Launch Checklist</div>
              {[
                { label: 'Sequence name set', ok: !!name },
                { label: 'Sender email configured', ok: !!fromEmail },
                { label: 'Contacts uploaded', ok: contactCount > 0, detail: contactCount > 0 ? `${contactCount} contacts` : 'Upload a CSV or add manually' },
                { label: 'Initial email written', ok: !!emails[0]?.subject && !!emails[0]?.body },
                {
                  label: 'Initial send time set',
                  ok: !!emails[0]?.scheduled_at,
                  detail: emails[0]?.scheduled_at
                    ? formatIST(istLocalToUTC(emails[0].scheduled_at))
                    : 'Set a date/time'
                },
                { label: 'Follow-ups configured', ok: emails.length > 1, detail: emails.length > 1 ? `${emails.length - 1} follow-up(s)` : 'Optional' },
                { label: 'Attachment ready', ok: true, detail: attachFilename || 'None (optional)' },
              ].map(item => (
                <div key={item.label} className="flex-center gap-12" style={{ padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
                  <span style={{ fontSize: 16 }}>{item.ok ? '✅' : '⚠️'}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: 13 }}>{item.label}</div>
                    {item.detail && <div className="text-xs" style={{ marginTop: 2 }}>{item.detail}</div>}
                  </div>
                </div>
              ))}
            </div>

            <div className="card" style={{ marginBottom: 20 }}>
              <div style={{ fontWeight: 600, marginBottom: 8 }}>Sequence Summary</div>
              <div style={{ fontSize: 13, color: 'var(--text2)', lineHeight: 2 }}>
                <div>📧 From: <strong style={{ color: 'var(--text)' }}>{fromEmail || 'Not set'}</strong></div>
                <div>👥 Contacts: <strong style={{ color: 'var(--text)' }}>{contactCount}</strong></div>
                <div>📬 Steps: <strong style={{ color: 'var(--text)' }}>{emails.length} ({emails.length === 1 ? 'initial only' : `1 initial + ${emails.length - 1} follow-up(s)`})</strong></div>
                <div>📎 Attachment: <strong style={{ color: 'var(--text)' }}>{attachFilename || 'None'}</strong></div>
                <div>✍️ Signature: <strong style={{ color: 'var(--text)' }}>{includeSignature ? 'Included' : 'Not included'}</strong></div>
                <div>👁 Open Tracking: <strong style={{ color: 'var(--text)' }}>{openTracking ? 'Enabled' : 'Disabled'}</strong></div>
              </div>
            </div>

            {(!fromEmail || contactCount === 0 || !emails[0]?.subject || !emails[0]?.scheduled_at) ? (
              <div className="alert alert-warning">
                ⚠️ Please complete all required fields before launching
              </div>
            ) : (
              <button className="btn btn-primary btn-lg" style={{ width: '100%' }} onClick={launch}>
                🚀 Launch Sequence — Send to {contactCount} Contacts
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
